import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Download,
  FileText,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import SearchableDropdown from '../SearchableDropdown';
import TimeRangePicker from './TimeRangePicker';
import {
  getTenantTransactionHistory,
  exportTenantHistory,
  type AgentCreditLedgerRow,
  type AgentCreditTransactionType,
  type AgentCreditOperation,
  type TenantHistoryFilters,
  type TimeRangeValue,
  resolveTimeRange,
} from '../../services/aiUsageApi';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
/**
 * TransactionHistoryTable — shared history view component.
 *
 * Prop contract lives in @platform/shared-types
 * (transaction-history-props.ts) so that Wave 2 (platform-app) can
 * implement a parallel copy with the same interface. This copy targets
 * super-admin endpoints when `context='admin'` and tenant-facing
 * endpoints when `context='self'` — the current super-admin UI only
 * uses 'admin'.
 *
 * Standards compliance:
 *  - No window.alert / window.confirm / window.prompt (feedback rule)
 *  - Dropdowns use SearchableDropdown (feedback rule)
 *  - Images would use ShimmerImage if present (no images in this table)
 *  - 44px touch targets on action buttons
 *  - Environment-agnostic: no super-admin-only imports outside
 *    ../../services/aiUsageApi, which is a pure fetch wrapper and is the
 *    one call site Wave 2 will parallel-copy.
 */

export interface TransactionHistoryTableProps {
  tenantId: string;
  context: 'admin' | 'self';
  authHeader?: string;
  initialTimeRange?: TimeRangeValue;
  onTimeRangeChange?: (range: TimeRangeValue) => void;
  className?: string;
  tenantDisplayName?: string;
}

type SortKey = 'createdAt' | 'creditsAmount';
type SortDir = 'asc' | 'desc';

const TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All types' },
  { value: 'CONSUME', label: 'Consume' },
  { value: 'PURCHASE', label: 'Purchase' },
  { value: 'ADMIN_GRANT', label: 'Admin grant' },
  { value: 'ADMIN_DEDUCT', label: 'Admin deduct' },
  { value: 'REFUND', label: 'Refund' },
];

const OP_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All operations' },
  { value: 'analytics_query', label: 'Analytics query' },
  { value: 'product_assist', label: 'Product assist' },
  { value: 'description_only', label: 'Description generation' },
  { value: 'image_search_only', label: 'Image search' },
  { value: 'inventory_forecast', label: 'Inventory alerts (forecast)' },
];

const PAGE_SIZE = 20;

const TransactionHistoryTable: React.FC<TransactionHistoryTableProps> = ({
  tenantId,
  context,
  authHeader,
  initialTimeRange,
  onTimeRangeChange,
  className,
  tenantDisplayName,
}) => {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(initialTimeRange ?? { preset: '30d' });
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [opFilter, setOpFilter] = useState<string>('all');
  const [items, setItems] = useState<AgentCreditLedgerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [pageCursor, setPageCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [exportingFormat, setExportingFormat] = useState<'csv' | 'pdf' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const displayName = tenantDisplayName || tenantId;

  const filters = useMemo<TenantHistoryFilters>(() => {
    const r = resolveTimeRange(timeRange);
    return {
      fromIso: r.fromIso,
      toIso: r.toIso,
      types:
        typeFilter === 'all'
          ? undefined
          : [typeFilter as AgentCreditTransactionType],
      operations:
        opFilter === 'all' ? undefined : [opFilter as AgentCreditOperation],
      limit: PAGE_SIZE,
      exclusiveStartKey: pageCursor ?? undefined,
    };
  }, [timeRange, typeFilter, opFilter, pageCursor]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTenantTransactionHistory(tenantId, filters, authHeader);
      setItems(res.items || []);
      setNextCursor(res.lastEvaluatedKey ?? null);
      setHasMore(Boolean(res.lastEvaluatedKey));
    } catch (err: any) {
      setError(err?.message || 'Failed to load history');
      setItems([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [tenantId, filters, authHeader]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, timeRange.preset, timeRange.fromIso, timeRange.toIso, typeFilter, opFilter, pageCursor]);

  const handleTimeRangeChange = (v: TimeRangeValue) => {
    setTimeRange(v);
    setCursors([null]);
    setPageCursor(null);
    onTimeRangeChange?.(v);
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'createdAt') cmp = a.createdAt.localeCompare(b.createdAt);
      else if (sortKey === 'creditsAmount') cmp = a.creditsAmount - b.creditsAmount;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sortKey, sortDir]);

  const handleNext = () => {
    if (!nextCursor) return;
    setCursors((c) => [...c, nextCursor]);
    setPageCursor(nextCursor);
  };

  const handlePrev = () => {
    if (cursors.length < 2) return;
    const nextStack = cursors.slice(0, -1);
    setCursors(nextStack);
    setPageCursor(nextStack[nextStack.length - 1] ?? null);
  };

  const handleExport = async (format: 'csv' | 'pdf') => {
    setExportingFormat(format);
    setExportError(null);
    try {
      const blob = await exportTenantHistory(tenantId, format, filters, authHeader);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credits-history-${tenantId}-${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setExportError(err?.message || 'Export failed');
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <div
      className={`bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden ${className ?? ''}`}
    >
      {/* Header + filters */}
      <div className="px-4 py-3 border-b border-stone-100 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-stone-900">Transaction History</h3>
          <p className="text-[11px] text-stone-500">
            {displayName} · {context === 'admin' ? 'Admin view' : 'Tenant view'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TimeRangePicker value={timeRange} onChange={handleTimeRangeChange} compact />
          <div className="w-[160px]">
            <SearchableDropdown
              options={TYPE_OPTIONS}
              value={typeFilter}
              onChange={(v) => {
                setTypeFilter(v || 'all');
                setCursors([null]);
                setPageCursor(null);
              }}
              placeholder="All types"
              searchPlaceholder="Type…"
              compact
            />
          </div>
          <div className="w-[180px]">
            <SearchableDropdown
              options={OP_OPTIONS}
              value={opFilter}
              onChange={(v) => {
                setOpFilter(v || 'all');
                setCursors([null]);
                setPageCursor(null);
              }}
              placeholder="All operations"
              searchPlaceholder="Operation…"
              compact
            />
          </div>
          <button
            type="button"
            onClick={() => handleExport('csv')}
            disabled={exportingFormat !== null}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-stone-100 text-stone-700 hover:bg-stone-200 min-h-[40px] disabled:opacity-50"
          >
            {exportingFormat === 'csv' ? (
              <RubyGemMark size={12} drawing color="currentColor" />
            ) : (
              <Download size={12} />
            )}
            CSV
          </button>
          <button
            type="button"
            onClick={() => handleExport('pdf')}
            disabled={exportingFormat !== null}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-stone-100 text-stone-700 hover:bg-stone-200 min-h-[40px] disabled:opacity-50"
          >
            {exportingFormat === 'pdf' ? (
              <RubyGemMark size={12} drawing color="currentColor" />
            ) : (
              <FileText size={12} />
            )}
            PDF
          </button>
        </div>
      </div>

      {exportError && (
        <div className="mx-4 mt-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          <span>{exportError}</span>
        </div>
      )}

      {error ? (
        <div className="p-6 text-center text-sm text-rose-600 bg-rose-50 border-y border-rose-100">
          <AlertTriangle size={16} className="inline mr-1 -mt-0.5" />
          {error}
        </div>
      ) : loading ? (
        <RubyLoader variant="section" label="Loading transactions…" />
      ) : sortedItems.length === 0 ? (
        <div className="p-8 text-center text-sm text-stone-500">
          No transactions in the selected range.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="px-3 py-2 text-left w-6" />
                <th className="px-3 py-2 text-left text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                  <button
                    type="button"
                    onClick={() => handleSort('createdAt')}
                    className="flex items-center gap-1 hover:text-stone-700"
                  >
                    Date
                    {sortKey === 'createdAt' ? (
                      sortDir === 'asc' ? (
                        <ArrowUp size={10} />
                      ) : (
                        <ArrowDown size={10} />
                      )
                    ) : (
                      <ArrowUpDown size={10} />
                    )}
                  </button>
                </th>
                <th className="px-3 py-2 text-left text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                  Type
                </th>
                <th className="px-3 py-2 text-left text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                  Detail
                </th>
                <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                  <button
                    type="button"
                    onClick={() => handleSort('creditsAmount')}
                    className="flex items-center gap-1 hover:text-stone-700 ml-auto"
                  >
                    Credits
                    {sortKey === 'creditsAmount' ? (
                      sortDir === 'asc' ? (
                        <ArrowUp size={10} />
                      ) : (
                        <ArrowDown size={10} />
                      )
                    ) : (
                      <ArrowUpDown size={10} />
                    )}
                  </button>
                </th>
                <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide hidden md:table-cell">
                  Balance
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {sortedItems.map((row) => {
                const isExpanded = expandedRow === row.sk;
                return (
                  <React.Fragment key={row.sk}>
                    <tr
                      className="hover:bg-stone-50 cursor-pointer"
                      onClick={() => setExpandedRow(isExpanded ? null : row.sk)}
                    >
                      <td className="px-3 py-2.5 text-stone-400">
                        {isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs font-mono text-stone-700 whitespace-nowrap">
                        {new Date(row.createdAt).toLocaleString(undefined, {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </td>
                      <td className="px-3 py-2.5">
                        <TypeBadge type={row.type} />
                      </td>
                      <td className="px-3 py-2.5 text-xs text-stone-700">
                        {row.operation && <span className="font-mono">{row.operation}</span>}
                        {row.bundleLabel && <span>{row.bundleLabel}</span>}
                        {row.reason && <span className="text-stone-500"> · {row.reason}</span>}
                        {row.grantedBy && (
                          <span className="text-stone-400 text-[11px]"> by {row.grantedBy}</span>
                        )}
                      </td>
                      <td
                        className={`px-3 py-2.5 text-xs font-semibold text-right whitespace-nowrap ${
                          row.creditsAmount >= 0 ? 'text-emerald-700' : 'text-rose-700'
                        }`}
                      >
                        {row.creditsAmount >= 0 ? '+' : ''}
                        {row.creditsAmount}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-stone-500 text-right hidden md:table-cell">
                        {row.balanceAfter}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-stone-50">
                        <td />
                        <td colSpan={5} className="px-3 py-3">
                          <ExpandedRowDetail row={row} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {(sortedItems.length > 0 || cursors.length > 1) && (
        <div className="px-4 py-3 border-t border-stone-100 flex items-center justify-between text-xs text-stone-500">
          <div>
            Page {cursors.length} · {sortedItems.length} rows
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handlePrev}
              disabled={cursors.length < 2 || loading}
              className="px-3 py-2 rounded-lg bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-40 min-h-[36px]"
            >
              ← Prev
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={!hasMore || loading}
              className="px-3 py-2 rounded-lg bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-40 min-h-[36px]"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const TypeBadge: React.FC<{ type: AgentCreditLedgerRow['type'] }> = ({ type }) => {
  const cfg: Record<string, { label: string; color: string }> = {
    CONSUME: { label: 'Consume', color: 'bg-rose-100 text-rose-700' },
    PURCHASE: { label: 'Purchase', color: 'bg-emerald-100 text-emerald-700' },
    ADMIN_GRANT: { label: 'Grant', color: 'bg-blue-100 text-blue-700' },
    ADMIN_DEDUCT: { label: 'Deduct', color: 'bg-rose-100 text-rose-700' },
    REFUND: { label: 'Refund', color: 'bg-amber-100 text-amber-700' },
  };
  const c = cfg[type] || { label: type, color: 'bg-stone-100 text-stone-700' };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded ${c.color}`}>
      {c.label}
    </span>
  );
};

const ExpandedRowDetail: React.FC<{ row: AgentCreditLedgerRow }> = ({ row }) => {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className="text-xs text-stone-700 space-y-2">
      {row.breakdown && row.breakdown.length > 0 && (
        <div>
          <div className="text-[11px] font-medium text-stone-500 uppercase tracking-wide mb-1">
            Per-API breakdown
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-stone-400 uppercase">
                <th className="text-left pr-3">API</th>
                <th className="text-right pr-3">In tokens</th>
                <th className="text-right pr-3">Out tokens</th>
                <th className="text-right pr-3">Real cost</th>
                <th className="text-right pr-3">Burn</th>
                <th className="text-right">Credits</th>
              </tr>
            </thead>
            <tbody>
              {row.breakdown.map((b, i) => {
                const detail = (b.detail || {}) as Record<string, unknown>;
                const inTok = detail.input_tokens as number | undefined;
                const outTok = detail.output_tokens as number | undefined;
                // Non-token meters (vision / grounding / cse) carry a unit count
                // instead of tokens — surface whatever the call actually used.
                const units =
                  (detail.visionCalls as number | undefined) ??
                  (detail.grounding_calls as number | undefined) ??
                  (detail.calls as number | undefined);
                return (
                  <tr key={i} className="border-t border-stone-200">
                    <td className="py-1 pr-3 font-mono text-stone-600">{b.api}</td>
                    <td className="py-1 pr-3 text-right">
                      {inTok != null ? inTok.toLocaleString() : units != null ? `${units} call${units === 1 ? '' : 's'}` : '—'}
                    </td>
                    <td className="py-1 pr-3 text-right">{outTok != null ? outTok.toLocaleString() : '—'}</td>
                    <td className="py-1 pr-3 text-right">${(b.costUsd ?? 0).toFixed(4)}</td>
                    <td className="py-1 pr-3 text-right">{b.burnRate != null ? `${b.burnRate}×` : '—'}</td>
                    <td className="py-1 text-right font-semibold">{b.credits ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {row.costUsd !== undefined && (
            <div className="mt-1.5 text-[11px] text-stone-500">
              Total upstream cost: ${row.costUsd.toFixed(4)}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-[11px]">
        {row.requestId && (
          <div>
            <span className="text-stone-400">Request ID: </span>
            <span className="font-mono text-stone-700">{row.requestId}</span>
          </div>
        )}
        {row.stripePaymentIntentId && (
          <div>
            <span className="text-stone-400">Payment Intent: </span>
            <span className="font-mono text-stone-700">{row.stripePaymentIntentId}</span>
          </div>
        )}
        {row.refundOfTxnId && (
          <div>
            <span className="text-stone-400">Refund of: </span>
            <span className="font-mono text-stone-700">{row.refundOfTxnId}</span>
          </div>
        )}
        <div>
          <span className="text-stone-400">Balance before: </span>
          <span className="text-stone-700">{row.balanceBefore}</span>
        </div>
        <div>
          <span className="text-stone-400">Balance after: </span>
          <span className="text-stone-700">{row.balanceAfter}</span>
        </div>
      </div>

      <div className="pt-1">
        <button
          type="button"
          onClick={() => setShowRaw((s) => !s)}
          className="text-[11px] font-medium text-rose-600 hover:text-rose-700"
        >
          {showRaw ? 'Hide raw JSON' : 'View raw JSON'}
        </button>
        {showRaw && (
          <pre className="mt-1.5 p-2 bg-stone-100 rounded text-[10px] text-stone-700 overflow-x-auto max-h-48">
            {JSON.stringify(row, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};

export default TransactionHistoryTable;
